import { useState } from 'react'

import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useUpdateEffect } from 'ahooks'
import { Button, Flex, Input } from 'antd'
import { createStyles } from 'antd-style'

import { ConfirmInputProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  container: {
    width: '100%'
  }
}))

export const ConfirmInput = <T extends string | number>({
  /* -------------------------------------------------------------------------- */
  /*                                 Basic Props                                */
  /* -------------------------------------------------------------------------- */
  className,
  style,
  /* -------------------------------------------------------------------------- */
  /*                             ConfirmInput Props                             */
  /* -------------------------------------------------------------------------- */
  autoClear,
  trigger = 'confirm',
  value: externalValue,
  getInputComponent,
  getConfirmComponent,
  onChange: externalOnChange,
  onCancel: externalOnCancel,
  onConfirm: externalOnConfirm,
  /* -------------------------------------------------------------------------- */
  /*                                 Input Props                                */
  /* -------------------------------------------------------------------------- */
  disabled,
  allowClear = true,
  autoFocus = true
}: ConfirmInputProps<T>) => {
  const [value, setValue] = useState(externalValue)

  // 同步外界的值
  useUpdateEffect(() => {
    setValue(externalValue)
  }, [externalValue])

  const onCancel = () => {
    externalOnCancel?.()
    setValue(externalValue)
  }

  const onConfirm = async () => {
    externalOnConfirm?.(value)
    if (trigger === 'confirm') {
      externalOnChange?.(value)
    }
    if (autoClear) {
      setValue(undefined)
    }
  }

  const customComponentProps = {
    value,
    onCancel,
    onConfirm,
    onChange(value?: T | null) {
      setValue(value ?? undefined)
      if (trigger === 'change') {
        externalOnChange?.(value ?? undefined)
      }
    }
  }

  const inputComponent = getInputComponent?.(customComponentProps) ?? (
    <Input
      allowClear={allowClear}
      autoFocus={autoFocus}
      disabled={disabled}
      value={value}
      onChange={e => setValue(e.target.value as T)}
      onPressEnter={onConfirm}
    />
  )

  const confirmComponent = getConfirmComponent?.(customComponentProps) ?? (
    <Button icon={<CheckOutlined />} type='default' onClick={onConfirm} />
  )

  const cancelComponent = (
    <Button icon={<CloseOutlined />} type='default' onClick={onCancel} />
  )

  const btnVisible = !disabled

  const { cx, styles } = useStyles()

  return (
    <Flex className={cx(styles.container, className)} gap={4} style={style}>
      {inputComponent}
      {btnVisible && trigger === 'confirm' && (
        <>
          {confirmComponent}
          {cancelComponent}
        </>
      )}
    </Flex>
  )
}
